import styled from '@emotion/styled';
import { css } from '@emotion/react';

import theme from 'styles/theme';

export type TabsVariant = 'pill' | 'underline';
export type TabsSize = 'sm' | 'md';

type ListProps = {
  $variant: TabsVariant;
  $fullWidth?: boolean;
  $borderColor?: string;
};

type TabProps = {
  $variant: TabsVariant;
  $size: TabsSize;
  $active?: boolean;
  $disabled?: boolean;
  $color?: string;
  $activeColor?: string;
  $activeBackgroundColor?: string;
  $borderColor?: string;
};

const sizeStyles: Record<TabsSize, ReturnType<typeof css>> = {
  sm: css`
    height: 28px;
    padding: 0 12px;
    font-size: 12px;
  `,
  md: css`
    height: 34px;
    padding: 0 16px;
    font-size: 14px;
  `,
};

export const Container = styled.div<{ $fullWidth?: boolean }>`
  display: inline-flex;
  flex-direction: column;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
`;

export const List = styled('div', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<ListProps>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};

  ${({ $variant, $borderColor }) =>
    $variant === 'underline' &&
    css`
      border-bottom: 1px solid ${$borderColor || '#E8E8E8'};
    `}
`;

export const TabButton = styled('button', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<TabProps>`
  border: none;
  background: transparent;
  font-family: 'Mangueira', sans-serif;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s ease, color 0.2s ease, opacity 0.2s ease;
  color: ${({ $color }) => $color || theme.colors.gray};

  ${({ $size }) => sizeStyles[$size]}

  ${({ $variant, $active, $activeColor, $activeBackgroundColor, $borderColor }) =>
    $variant === 'pill' &&
    css`
      border-radius: 999px;
      background: ${$active
        ? $activeBackgroundColor || theme.colors.mediumPurple
        : '#F0F0F0'};
      color: ${$active ? $activeColor || '#fff' : 'inherit'};
    `}

  ${({ $variant, $active, $activeColor, $borderColor }) =>
    $variant === 'underline' &&
    css`
      border-radius: 0;
      border-bottom: 2px solid
        ${$active ? $activeColor || theme.colors.mediumPurple : 'transparent'};
      color: ${$active ? $activeColor || theme.colors.blue : 'inherit'};
      margin-bottom: -1px;
    `}

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const Panel = styled.div`
  width: 100%;
`;
